
import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'InteriorDesigner',
  name: 'Exquisite Spaces',
  description:
    "Abuja's premier luxury interior design studio transforming residential and commercial spaces in Nigeria into extraordinary living art.",
  url: siteUrl,
  telephone: '+2347062910646',
  email: 'exquisitespacesnigeria@gmail.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '363 Ifeanyi Ugwanyi Crescent',
    addressLocality: 'Wuye, Abuja',
    addressRegion: 'FCT',
    addressCountry: 'NG',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 9.0765,
    longitude: 7.3986,
  },
  areaServed: {
    '@type': 'City',
    name: 'Abuja',
  },
  priceRange: '\u20A6\u20A6\u20A6',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Interior Design Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Residential Interior Design',
          description:
            'Full-service luxury residential interior design in Abuja, Nigeria.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Commercial Interior Design',
          description:
            'Bespoke commercial interior design for offices, hotels, and retail spaces in Nigeria.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Space Planning',
          description:
            'Expert space planning and layout optimization for homes and businesses in Abuja.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Color Consultation',
          description:
            'Professional color consultation services to enhance your interior spaces.',
        },
      },
      {
        '@type': 'Offer',
        name: 'Design Consultation — 40% Off',
        description:
          'Book a one-on-one design consultation with our lead designer. Limited-time 40% discount: \u20A6600,000 (regular price \u20A61,000,000).',
        price: '600000',
        priceCurrency: 'NGN',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '600000',
          priceCurrency: 'NGN',
          referenceQuantity: {
            '@type': 'QuantitativeValue',
            value: '1',
            unitText: 'session',
          },
        },
        eligibleQuantity: {
          '@type': 'QuantitativeValue',
          minValue: 1,
        },
      },
    ],
  },
  sameAs: [],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Exquisite Spaces',
  url: siteUrl,
  description:
    'Luxury interior design studio in Abuja, Nigeria specializing in residential and commercial spaces.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteUrl}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What interior design services does Exquisite Spaces offer in Abuja?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Exquisite Spaces offers full-service residential interior design, commercial interior design, space planning, color consultation, and bespoke furniture sourcing for clients in Abuja and across Nigeria.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I book a consultation with Exquisite Spaces?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can book a consultation by clicking the "Book a Consultation at 40% Off" button on our website, filling in your details, and selecting a preferred date and time. The consultation fee is \u20A6600,000 (40% off the regular \u20A61,000,000 rate). Our team will confirm your appointment promptly.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does a design consultation cost at Exquisite Spaces?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A design consultation at Exquisite Spaces is currently available at a limited-time 40% discount, bringing the fee to \u20A6600,000 (down from the regular price of \u20A61,000,000). This is a 30-minute session with our lead designer.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Exquisite Spaces work on commercial projects in Nigeria?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Exquisite Spaces handles both residential and commercial interior design projects across Nigeria, including offices, boutique hotels, restaurants, and retail spaces.',
      },
    },
  ],
};

export default function SeoJsonLd() {
  useEffect(() => {
    const schemas = [localBusinessSchema, websiteSchema, faqSchema];
    const scriptIds = ['schema-local-business', 'schema-website', 'schema-faq'];

    schemas.forEach((schema, i) => {
      let script = document.getElementById(scriptIds[i]) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptIds[i];
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    });

    return () => {
      scriptIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return null;
}